import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ArrowRight, Bell, FileDown, Loader2, Paperclip, Plus, Search, Send, Settings2, Users } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useChat } from "@/context/ChatContext";
import { api, errMsg, uid, ROLE_LABEL } from "@/lib/api";
import { Empty, Loading, Modal } from "@/components/common";

const time = (iso) => new Date(iso).toLocaleString("ar-DZ", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
const Avatar = ({ name, online }) => (
  <div className="relative w-10 h-10 rounded-full bg-[#1A4D2E] text-white grid place-items-center font-bold shrink-0">{(name || "?").trim()[0]}
    {online && <span className="absolute bottom-0 left-0 w-3 h-3 rounded-full bg-emerald-400 border-2 border-white" />}</div>
);

function Attachment({ m }) {
  const [url, setUrl] = useState(null);
  const a = m.attachment;
  useEffect(() => {
    if (!a.is_image) return undefined;
    let u, alive = true;
    api.get(`/chat/files/${m.id}`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); if (alive) setUrl(u); }).catch(() => {});
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [m.id, a.is_image]);
  const download = async () => {
    try { const r = await api.get(`/chat/files/${m.id}`, { responseType: "blob" }); const u = URL.createObjectURL(r.data); const l = document.createElement("a"); l.href = u; l.download = a.name; l.click(); URL.revokeObjectURL(u); } catch (e) { toast.error(errMsg(e)); }
  };
  if (a.is_image) return url ? <img src={url} alt={a.name} className="max-w-[240px] max-h-64 rounded-lg cursor-pointer" onClick={() => window.open(url)} data-testid="chat-image" /> : <div className="w-40 h-28 bg-black/10 rounded-lg animate-pulse" />;
  return <button onClick={download} className="flex items-center gap-2 underline" data-testid="chat-file"><FileDown className="w-4 h-4" /> {a.name}</button>;
}

function GroupDialog({ room, users, onClose, done }) {
  const [name, setName] = useState(room?.name || "");
  const [ids, setIds] = useState(room?.member_ids || []);
  const toggle = (id) => setIds(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  const byRole = (role) => setIds([...new Set([...ids, ...users.filter((u) => u.role === role).map((u) => u.id)])]);
  const save = async () => {
    try { room ? await api.put(`/chat/groups/${room.id}`, { name, member_ids: ids }) : await api.post("/chat/groups", { name, member_ids: ids }); toast.success("تم الحفظ"); done(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal open onOpenChange={(o) => !o && onClose()} title={room ? "إدارة المجموعة" : "مجموعة جديدة"} footer={<button className="k-btn-primary" onClick={save} disabled={name.trim().length < 2} data-testid="group-save">حفظ</button>}>
      <div className="space-y-3" data-testid="group-dialog">
        <input className="k-input" placeholder="اسم المجموعة (مثلًا: المبيعات)" value={name} onChange={(e) => setName(e.target.value)} data-testid="group-name" />
        <div className="flex flex-wrap gap-1 text-xs">إضافة حسب الدور:{Object.entries(ROLE_LABEL).map(([k, v]) => <button key={k} className="k-btn-outline k-btn-sm" onClick={() => byRole(k)} data-testid={`group-role-${k}`}>{v}</button>)}</div>
        <div className="max-h-64 overflow-y-auto border rounded-xl p-2 space-y-1">
          {users.map((u) => <label key={u.id} className="flex items-center gap-2 text-sm cursor-pointer"><input type="checkbox" checked={ids.includes(u.id)} onChange={() => toggle(u.id)} data-testid={`group-member-${u.id}`} /> {u.full_name} <span className="text-xs text-slate-500">{ROLE_LABEL[u.role]}</span></label>)}
        </div>
      </div>
    </Modal>
  );
}

function Pane({ room, me, onBack, canManage, onManage }) {
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const { subscribe, refreshUnread } = useChat();
  const end = useRef(null);
  const file = useRef(null);
  const read = useCallback(() => api.post(`/chat/rooms/${room.id}/read`).then(refreshUnread).catch(() => {}), [room.id, refreshUnread]);
  useEffect(() => { setMsgs(null); api.get(`/chat/rooms/${room.id}/messages`).then((r) => setMsgs(r.data)); read(); }, [room.id, read]);
  useEffect(() => subscribe((ev) => {
    if (ev.type === "message" && ev.room_id === room.id) { setMsgs((m) => (m && !m.some((x) => x.id === ev.message.id) ? [...m, ev.message] : m)); read(); }
  }), [subscribe, room.id, read]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs]);
  const add = (m) => setMsgs((o) => (o.some((x) => x.id === m.id) ? o : [...o, m]));
  const send = async (e) => {
    e.preventDefault(); if (!text.trim()) return;
    setBusy(true);
    try { add((await api.post(`/chat/rooms/${room.id}/messages`, { text, client_id: uid() })).data); setText(""); } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  const upload = async (e) => {
    const f = e.target.files[0]; e.target.value = ""; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    setBusy(true);
    try { add((await api.post(`/chat/rooms/${room.id}/files`, fd, { timeout: 120000 })).data); } catch (ex) { toast.error(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col h-full" data-testid="chat-pane">
      <div className="flex items-center gap-3 p-3 border-b bg-white">
        <button className="md:hidden" onClick={onBack} data-testid="chat-back"><ArrowRight className="w-5 h-5" /></button>
        <Avatar name={room.name} />
        <div className="flex-1"><div className="font-bold" data-testid="chat-room-title">{room.name}</div><div className="text-xs text-slate-500">{room.kind === "general" ? "جميع العمال" : room.kind === "group" ? `${room.members.length} عضو` : "محادثة خاصة"}</div></div>
        {room.kind === "group" && canManage && <button className="k-btn-outline k-btn-sm" onClick={onManage} data-testid="group-manage"><Settings2 className="w-4 h-4" /></button>}
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-[#F6F1E7]" data-testid="chat-messages">
        {!msgs ? <Loading /> : !msgs.length ? <Empty text="لا توجد رسائل بعد" /> : msgs.map((m) => {
          const mine = m.sender_id === me;
          return (
            <div key={m.id} className={`flex gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid="chat-message">
              {!mine && <Avatar name={m.sender_name} />}
              <div className={`max-w-[78%] rounded-2xl px-3 py-2 shadow-sm ${mine ? "bg-[#1A4D2E] text-white" : "bg-white"}`}>
                {!mine && <div className="text-xs font-bold text-[#D97706]">{m.sender_name}</div>}
                {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                {m.attachment && <Attachment m={m} />}
                <div className={`text-[10px] mt-1 ${mine ? "text-emerald-100" : "text-slate-400"}`}>{time(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={end} />
      </div>
      <form onSubmit={send} className="p-2 border-t bg-white flex gap-2">
        <button type="button" className="k-btn-outline px-3" onClick={() => file.current.click()} disabled={busy} data-testid="chat-attach"><Paperclip className="w-5 h-5" /></button>
        <input ref={file} type="file" className="hidden" onChange={upload} data-testid="chat-file-input" />
        <input className="k-input flex-1" placeholder="اكتب رسالة…" value={text} onChange={(e) => setText(e.target.value)} data-testid="chat-input" />
        <button className="k-btn-primary px-4" disabled={busy || !text.trim()} data-testid="chat-send">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}</button>
      </form>
    </div>
  );
}

export default function Chat() {
  const { user } = useAuth();
  const { subscribe, enablePush } = useChat();
  const [rooms, setRooms] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [sp, setSp] = useSearchParams();
  const [dlg, setDlg] = useState(null);
  const active = rooms?.find((r) => r.id === sp.get("room"));
  const canManage = user.role === "admin" || user.effective_permissions?.includes("manage_chat");
  const load = useCallback(() => { api.get("/chat/rooms").then((r) => setRooms(r.data)); api.get("/chat/users").then((r) => setUsers(r.data)); }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => subscribe((ev) => { if (ev.type === "rooms" || (ev.type === "message")) load(); }), [subscribe, load]);
  const open = (id) => setSp({ room: id });
  const direct = async (u) => { try { const { data } = await api.post("/chat/direct", { user_id: u.id }); await load(); open(data.id); setQ(""); } catch (e) { toast.error(errMsg(e)); } };
  const push = async () => {
    const r = await enablePush().catch(() => "error");
    toast[r === "ok" ? "success" : "info"]({ ok: "تم تفعيل الإشعارات على هذا الجهاز", denied: "رُفض إذن الإشعارات من المتصفح", unsupported: "هذا المتصفح لا يدعم الإشعارات (على آيفون ثبّت التطبيق على الشاشة الرئيسية أولًا)", error: "تعذر تفعيل الإشعارات" }[r]);
  };
  const ql = q.trim().toLowerCase();
  const fr = (rooms || []).filter((r) => !ql || (r.name || "").toLowerCase().includes(ql));
  const fu = ql ? users.filter((u) => u.full_name.toLowerCase().includes(ql)) : [];
  return (
    <div className="k-card overflow-hidden h-[calc(100vh-7rem)] flex" data-testid="chat-page">
      <aside className={`w-full md:w-80 border-l flex flex-col ${active ? "hidden md:flex" : "flex"}`}>
        <div className="p-3 space-y-2 border-b">
          <div className="flex items-center gap-2"><h1 className="font-extrabold text-lg flex-1 text-[#1A4D2E]" data-testid="page-title">الدردشة</h1>
            <button className="k-btn-outline k-btn-sm" onClick={push} title="تفعيل الإشعارات" data-testid="chat-enable-push"><Bell className="w-4 h-4" /></button>
            {canManage && <button className="k-btn-primary k-btn-sm" onClick={() => setDlg({})} data-testid="chat-new-group"><Plus className="w-4 h-4" /> مجموعة</button>}</div>
          <div className="relative"><Search className="w-4 h-4 absolute right-3 top-3 text-slate-400" /><input className="k-input pr-9" placeholder="ابحث عن مستخدم أو محادثة" value={q} onChange={(e) => setQ(e.target.value)} data-testid="chat-search" /></div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {!rooms ? <Loading /> : fr.map((r) => (
            <button key={r.id} onClick={() => open(r.id)} className={`w-full text-right p-3 flex items-center gap-3 border-b hover:bg-[#F6F1E7] ${active?.id === r.id ? "bg-[#F6F1E7]" : ""}`} data-testid={`chat-room-${r.kind}-${r.id.slice(0, 6)}`}>
              {r.kind === "direct" ? <Avatar name={r.name} online={users.find((u) => u.id === r.other_id)?.online} /> : <div className="w-10 h-10 rounded-full bg-[#D97706] text-white grid place-items-center"><Users className="w-5 h-5" /></div>}
              <div className="flex-1 min-w-0"><div className="font-bold truncate">{r.name}</div><div className="text-xs text-slate-500 truncate">{r.last_sender ? `${r.last_sender}: ` : ""}{r.last_preview || "—"}</div></div>
              {r.unread > 0 && <span className="min-w-6 h-6 px-1.5 rounded-full bg-[#D97706] text-white text-xs grid place-items-center num" data-testid="chat-room-unread">{r.unread}</span>}
            </button>
          ))}
          {fu.length > 0 && <div className="p-2 text-xs font-bold text-slate-500">بدء محادثة خاصة</div>}
          {fu.map((u) => <button key={u.id} onClick={() => direct(u)} className="w-full text-right p-3 flex items-center gap-3 hover:bg-[#F6F1E7]" data-testid={`chat-user-${u.id}`}><Avatar name={u.full_name} online={u.online} /><div><div className="font-bold">{u.full_name}</div><div className="text-xs text-slate-500">{ROLE_LABEL[u.role]}</div></div></button>)}
          {!ql && <div className="p-3 text-xs text-slate-500">لبدء محادثة خاصة ابحث عن اسم المستخدم.</div>}
        </div>
      </aside>
      <section className={`flex-1 ${active ? "block" : "hidden md:block"}`}>
        {active ? <Pane room={active} me={user.id} onBack={() => setSp({})} canManage={canManage} onManage={() => setDlg({ room: active })} /> : <div className="h-full grid place-items-center text-slate-400">اختر محادثة</div>}
      </section>
      {dlg && <GroupDialog room={dlg.room} users={[{ id: user.id, full_name: `${user.full_name} (أنا)`, role: user.role }, ...users]} onClose={() => setDlg(null)} done={load} />}
    </div>
  );
}
